import { useContext, useEffect, useRef } from 'react';
import cx from 'classnames';
import { Root, Item, Trigger, Content } from '@radix-ui/react-accordion';
import {
  Heading,
  enums,
  Icon,
  Pill,
  Tooltip,
  utils as u,
  Button,
} from 'edrolo-ui';
import {
  Lesson,
  Attachment,
  Chunk,
  ActivityChunk,
  InteractiveActivityChunk,
} from '../../../../../../types/Assemblage';
import { LessonType } from 'edrolo/enums/LessonType';
import { AttachmentParent } from '../../../../../../enums/Attachments';
import { WaffleFlag } from 'edrolo/utils/Waffle';

import {
  ChunksCard,
  QuestionSetCard,
  ActivityCard,
  InteractiveActivityCard,
  AttachmentCard,
  Metadata,
} from '../../components';
import { extractLessonAttachments, getLessonMetadata } from './utils';
import { useAttachments } from 'edrolo/domains/attachments/hooks';
import s from './styles.scss';
import { ClassContext, UserContext } from 'edrolo/context';
import { Metadatum } from '../../types/Metadata';
import { TeacherProgressCircles, StudentProgressCircles } from './components';
import { useUserCourseProgress } from 'edrolo/domains/bySchoolClass';
import { SelfRatingDialog } from '../SelfRatingDialog';
import urlResolver from 'edrolo/utils/URLResolver';

interface LessonCardProps {
  lesson: Lesson;
  courseId: number;
  topicId: TopicId;
  insightsAvailabilityFlagName: string;
}

export const LessonCard = ({
  lesson,
  courseId,
  topicId,
  insightsAvailabilityFlagName,
}: LessonCardProps): JSX.Element | null => {
  const {
    data: { isTeacherView, studentNotesEnabled, showMetadata, classId },
  } = useContext(ClassContext);

  const { profile } = useContext(UserContext);

  const {
    data: lessonAttachmentsData,
    error: attachmentsError,
    isLoading: attachmentsLoading,
  } = useAttachments(
    classId,
    AttachmentParent.LESSON,
    lesson.id,
    isTeacherView
  );

  const {
    data: courseProgressData,
    error: courseProgressError,
    loading: courseProgressLoading,
  } = useUserCourseProgress(profile.user_id, +classId);

  const hasNotes =
    (!courseProgressError &&
      !courseProgressLoading &&
      courseProgressData.lessons_with_notes_taken.includes(lesson.id)) ||
    null;

  const attachmentsFromLesson: Attachment[] =
    (!attachmentsLoading &&
      !attachmentsError &&
      Object.values(lessonAttachmentsData.toJS())) ||
    [];

  const { lessonContentAttachments, attachmentContentItems } =
    extractLessonAttachments(attachmentsFromLesson);

  const activityChunks: Chunk[] = lesson.chunks.filter(
    (chunk) => chunk.activity
  );

  const interactiveActivityChunks: Chunk[] = lesson.chunks.filter(
    (chunk) => chunk.interactive_activity
  );

  if (attachmentsError) {
    console.error(attachmentsError);
  }

  const metadata: Metadatum[] = getLessonMetadata(
    lesson,
    activityChunks,
    interactiveActivityChunks,
    attachmentContentItems
  );

  const lessonCardAccordionRef = useRef(null);

  // TODO: use anchor <LessonCard id={lessonId}/> and check for url to set isOpen by default
  // Expand and scroll to the lesson card if the url has a '#<lessonId>' hash in it
  useEffect(() => {
    if (
      lessonCardAccordionRef?.current &&
      window.location.hash === `#${lesson.id}`
    ) {
      (lessonCardAccordionRef.current as HTMLButtonElement).click();
      (lessonCardAccordionRef.current as HTMLButtonElement).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [lessonCardAccordionRef, lesson.id]);

  const showStudentNotes = studentNotesEnabled && !isTeacherView && hasNotes;

  const hasChunks = lesson.chunk_count > 0;
  const showQuestionSet =
    lesson.question_set && lesson.question_set.is_published;
  const hasActivity = activityChunks.length > 0;
  const hasInteractiveActivity = interactiveActivityChunks.length > 0;
  const hasAttachments = attachmentContentItems.length > 0;

  // FIX: isEmpty shows "coming soon" when attachments are still loading
  const isEmpty =
    !hasChunks &&
    !showQuestionSet &&
    !hasActivity &&
    !hasInteractiveActivity &&
    !hasAttachments;
  const isPrac = lesson.lesson_type === LessonType.PRAC;
  const hasQuestionSet = lesson.question_set;
  const hasInsights = hasQuestionSet;
  const isStaff = profile.is_staff;
  const notStudent = isStaff || isTeacherView;

  const showLessonInsights = notStudent && hasInsights && !isPrac;

  return (
    <Root
      className={cx(s.lessonCard, {
        [s.highlight]:
          !lesson.is_published || lesson.question_set?.is_published === false, // avoid true if undefined
        [s.disabled]: isEmpty,
      })}
      type="single"
      collapsible
    >
      <Item value={`${lesson.id}`}>
        <header className={s.header} data-appcues="lesson-card">
          <div className={s.headerLeft}>
            <Trigger className={s.trigger} ref={lessonCardAccordionRef}>
              <div className={s.triggerLeft}>
                <div className={s.chevron}>
                  <Icon name={enums.Icons.CHEVRON_DOWN} />
                </div>
              </div>
              <div className={s.triggerRight}>
                <div>
                  <div className={s.titleBlock}>
                    {isPrac && (
                      <Pill
                        variant={enums.PillVariant.FADE}
                        color={enums.ColorHue.AMBER}
                      >
                        <Icon
                          name={enums.Icons.FLASK}
                          color={enums.ColorHue.AMBER}
                          className={u.mr1}
                        />
                        PRAC
                      </Pill>
                    )}
                    <Heading className={s.title} level={3}>
                      {lesson.title}
                    </Heading>

                    {showStudentNotes && (
                      <Tooltip tip="This lesson has reviewable notes">
                        <Pill
                          variant={enums.PillVariant.CIRCLE}
                          color={enums.ColorTheme.BACKGROUND}
                        >
                          <Icon
                            color={enums.ColorTheme.SECONDARY}
                            name={enums.Icons.ACTIVITY}
                          />
                        </Pill>
                      </Tooltip>
                    )}

                    {!lesson.is_published && (
                      <Pill color={enums.ColorHue.GREY}>Unpublished</Pill>
                    )}
                    {lesson.question_set?.is_published === false && ( // avoid true if undefined
                      <Pill color={enums.ColorHue.GREY}>Has unpublished</Pill>
                    )}
                    {lesson.is_coming_soon && (
                      <Pill color={enums.ColorHue.GREY}>Has coming soon</Pill>
                    )}
                    {isEmpty && (
                      <Pill color={enums.ColorHue.GREY}>Coming soon</Pill>
                    )}
                  </div>

                  {showMetadata && (
                    <Metadata className={s.metadata} metadata={metadata} />
                  )}
                </div>
              </div>
            </Trigger>
          </div>
          <div className={s.headerRight}>
            {isTeacherView ? (
              <>
                {showLessonInsights && (
                  <WaffleFlag name={insightsAvailabilityFlagName}>
                    <Button.Anchor
                      size={enums.Size.SMALL}
                      variant={enums.Variant.FADE}
                      color={enums.ColorTheme.SECONDARY}
                      href={urlResolver.reverse('class-lesson-insights-page', {
                        classId,
                        topicId,
                        lessonId: lesson.id,
                      })}
                    >
                      <Icon name={enums.Icons.MAGIC} className={u.mr1} />
                      Insights
                    </Button.Anchor>
                  </WaffleFlag>
                )}
                <TeacherProgressCircles lesson={lesson} />
              </>
            ) : (
              <>
                <SelfRatingDialog
                  userId={profile.user_id}
                  classId={classId}
                  lessonId={lesson.id}
                />
                <StudentProgressCircles lesson={lesson} />
              </>
            )}
          </div>
        </header>

        <Content className={s.content}>
          <div className={s.contentInner}>
            {lesson.chunk_count > 0 && (
              <ChunksCard
                lesson={lesson}
                attachments={lessonContentAttachments}
                showStudentNotes={!!showStudentNotes}
              />
            )}
            {lesson.question_set && (
              <QuestionSetCard
                topicId={topicId}
                questionSet={lesson.question_set}
              />
            )}
            {activityChunks.map((activityChunk) => (
              <ActivityCard
                key={activityChunk.id}
                activityChunk={activityChunk as ActivityChunk}
                lesson={lesson}
              />
            ))}
            {interactiveActivityChunks.map((chunk) => (
              <InteractiveActivityCard
                key={chunk.id}
                chunk={chunk as InteractiveActivityChunk}
                lesson={lesson}
              />
            ))}

            {!attachmentsLoading &&
              !attachmentsError &&
              attachmentContentItems.map((attachment: Attachment) => (
                <AttachmentCard
                  key={attachment.id}
                  attachment={attachment}
                  courseId={courseId}
                  classId={classId}
                />
              ))}
          </div>
        </Content>
      </Item>
    </Root>
  );
};
